import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateUpdateVehicleComplexityRuleRequest,
  ISingleVehicleComplexityRuleResponse,
  IVehicleComplexityMatchRequest,
  IVehicleComplexityMatchResponse,
  IVehicleComplexityRulesResponse,
} from "../models/VehicleComplexity";

export default function useVehicleComplexityRules() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries(["vehicleComplexityRules"]);

  /**
   * Every site's rules, not just ours. The API resolves `canEdit` per row, so the matrix can render
   * another site's grading read-only without comparing customer ids client-side.
   */
  const rules = useQuery({
    queryKey: ["vehicleComplexityRules"],
    queryFn: () => get<IVehicleComplexityRulesResponse>("/vehiclecomplexityrule"),
    staleTime: 1000 * 60,
  });

  const createRule = useMutation(
    (body: ICreateUpdateVehicleComplexityRuleRequest) =>
      post<ICreateUpdateVehicleComplexityRuleRequest, ISingleVehicleComplexityRuleResponse>(
        "/vehiclecomplexityrule",
        body
      ),
    { onSuccess: invalidate }
  );

  const updateRule = useMutation(
    ({ ruleId, body }: { ruleId: string; body: ICreateUpdateVehicleComplexityRuleRequest }) =>
      put<ICreateUpdateVehicleComplexityRuleRequest, ISingleVehicleComplexityRuleResponse>(
        `/vehiclecomplexityrule/${ruleId}`,
        body
      ),
    { onSuccess: invalidate }
  );

  const deleteRule = useMutation((ruleId: string) => del(`/vehiclecomplexityrule/${ruleId}`), {
    onSuccess: invalidate,
  });

  /** What a make and model would grade as right now, without writing anything. */
  const matchComplexity = useMutation((body: IVehicleComplexityMatchRequest) =>
    post<IVehicleComplexityMatchRequest, IVehicleComplexityMatchResponse>(
      "/vehiclecomplexityrule/match",
      body
    )
  );

  return {
    rules,
    createRule,
    updateRule,
    deleteRule,
    matchComplexity,
  };
}
